import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';

export interface NewPatientInfo {
  hospitalId?: string;
  hospitalName?: string;
  patientId: number;
  patientName: string;
  age: number;
  gender: string; // '男' または '女'
  departmentId?: string;
  departmentName: string;
  ward?: string;
  floor?: string;
  room: string;
  bed: string;
  measuringDate?: string;
  // 四つの三角形のリスクデータ
  assessment?: {
    level: string; // '高' | '中' | '低'
    score: number;
    deliriumRisk: string; // '高' | '中' | '低'
    fallRisk: string; // '高' | '中' | '低'
  };
  acuteChangeRisk?: {
    level: string; // '高' | '中' | '低'
    score: number;
  };
  locationRisk?: {
    level: string; // '高' | '中' | '低'
    score: number;
    zone: string; // '自室' | '廊下' | 'トイレ' | '他室'
  };
  sleepRisk?: {
    level: string; // '高' | '中' | '低'
    score: number;
  };
  totalScore?: number;
}

@Component({
  selector: 'app-new-patient-card',
  templateUrl: './new-patient-card.component.html',
  styleUrls: ['./new-patient-card.component.css']
})
export class NewPatientCardComponent implements OnInit {

  @Input()
  infos: NewPatientInfo[] = [];

  @Input()
  alertPatientIds: number[] = []; // アラート患者IDリスト

  // 患者詳細クリックイベント
  @Output()
  detailClicked = new EventEmitter<NewPatientInfo>();

  // 移動履歴クリックイベント（右側の三角形）
  @Output()
  movementHistoryClicked = new EventEmitter<NewPatientInfo>();

  // タイムスタディ（右上アイコン）
  @Output()
  timestudyClicked = new EventEmitter<NewPatientInfo>();

  iconPath = 'assets/images/icon/delirium/';

  constructor() {
  }

  ngOnInit(): void {
  }

  /**
   * 患者カード全体クリック（タイムスタディアイコンと位置三角形を除く）
   */
  onCardClick(info: NewPatientInfo, event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.closest('.patient-timestudy-icon')) {
      return;
    }
    if (target.closest('.tri-right')) {
      return;
    }
    this.detailClicked.emit(info);
  }

  /**
   * タイムスタディアイコンクリック
   */
  onTimestudyClick(info: NewPatientInfo, event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.timestudyClicked.emit(info);
  }

  /**
   * 患者詳細クリック（後方互換）
   */
  onDetailClick(info: NewPatientInfo): void {
    this.detailClicked.emit(info);
  }

  /**
   * 右側三角形（移動履歴）クリック
   */
  onMovementHistoryClick(info: NewPatientInfo, event: MouseEvent): void {
    event.stopPropagation(); // イベントのバブリングを阻止し、患者詳細popupのトリガーを防止
    event.preventDefault();
    this.movementHistoryClicked.emit(info);
  }

  /**
   * 患者がアラートリストに含まれているかチェック
   */
  isAlertPatient(patientId: number): boolean {
    return this.alertPatientIds.includes(patientId);
  }

  /**
   * スコアに基づいてリスクカラークラスを取得
   * @param score リスクスコア
   * @param isAcuteChange 急変リスクかどうか（急変リスクの0分は白色を表示）
   */
  getRiskColorClass(score: number, isAcuteChange: boolean = false): string {
    if (score === 0 && isAcuteChange) return 'risk-white'; // 急変リスクの0分は白色
    if (score >= 20) return 'risk-high';
    if (score >= 10) return 'risk-medium';
    if (score >= 5) return 'risk-low';
    return 'risk-none';
  }

  /**
   * 上方三角形のアイコンを取得（注意事項）
   * ドキュメントに基づく：
   * - score = 0 → 正常アイコン（チェックマーク）
   * - score > 0 → 得点項目のアイコン（せん妄または転倒リスク）
   * 
   * 判断ロジック：
   * - せん妄：スイッチON = 10点（deliriumRisk === '高'）
   * - 転倒リスク：0点/5点/10点/15点（fallRisk が存在し、score > 0 の場合、得点がある）
   */
  /**
   * 三角内の表示アイコン数に応じたレイアウト（上下左右の位置ではなく個数のみ）
   */
  triIconLayoutClass(iconCount: number): { 'tri-icon--single': boolean; 'tri-icon--multi': boolean } {
    return {
      'tri-icon--multi': iconCount >= 2,
      'tri-icon--single': iconCount < 2
    };
  }

  /**
   * 位置：現在は1件。将来2件になっても配列で揃える
   */
  getLocationIcons(info: NewPatientInfo): string[] {
    return [this.getLocationIcon(info.locationRisk?.zone || '自室')];
  }

  /**
   * 睡眠：現在は1件
   */
  getSleepIcons(info: NewPatientInfo): string[] {
    return [this.getSleepIcon(info.sleepRisk?.score || 0)];
  }

  /**
   * 急変：現在は1件
   */
  getAcuteChangeIcons(info: NewPatientInfo): string[] {
    return [this.getAcuteChangeIcon(info.acuteChangeRisk?.score || 0)];
  }

  getAssessmentIcons(info: NewPatientInfo): string[] {
    const icons: string[] = [];
    if (info.assessment) {
      const score = info.assessment.score || 0;
      
      // score = 0 の場合、正常アイコンを表示
      if (score === 0) {
        icons.push('normal.svg');
        return icons;
      }
      
      // score > 0 の場合、得点項目のアイコンを表示
      // せん妄リスク：スイッチON = 10点（deliriumRisk === '高'）
      const hasDeliriumRisk = info.assessment.deliriumRisk === '高';
      
      // 転倒リスク：得点がある場合（fallRisk が存在し、かつ score > 0）
      // score = 5 の場合、転倒リスク = 5点（低）の可能性がある
      // score >= 10 の場合、転倒リスク >= 10点（中/高）またはせん妄 = 10点
      const hasFallRisk = info.assessment.fallRisk && 
        info.assessment.fallRisk !== '' && 
        (info.assessment.fallRisk === '高' || 
         info.assessment.fallRisk === '中' || 
         (info.assessment.fallRisk === '低' && score === 5)); // score = 5 の場合、転倒リスク = 5点（低）
      
      if (hasDeliriumRisk) {
        icons.push('sennmo.svg');
      }
      if (hasFallRisk) {
        icons.push('fall.svg');
      }
      
      // どちらのアイコンも表示されない場合（データ不整合）、正常アイコンを表示
      if (icons.length === 0) {
        icons.push('normal.svg');
      }
    } else {
      icons.push('normal.svg');
    }
    return icons;
  }

  /**
   * 位置アイコンを取得
   */
  getLocationIcon(zone: string): string {
    if (zone === '廊下') {
      return 'hallway.svg';
    }
    if (zone === 'トイレ') {
      return 'toilet.svg';
    }
    if (zone === '他室') {
      return 'other_room.svg';
    }
    return 'normal.svg';
  }

  /**
   * 画像読み込みエラー処理
   */
  onImageError(event: Event, info: NewPatientInfo): void {
    // 位置アイコンの画像読み込みに失敗
  }

  /**
   * 睡眠アイコンを取得
   */
  getSleepIcon(score: number): string {
    return score === 0 ? 'in_bed.svg' : 'off_bed.svg';
  }

  /**
   * 急変リスクアイコンを取得
   */
  getAcuteChangeIcon(score: number): string {
    return score > 0 ? 'alert.svg' : 'normal.svg';
  }

  /**
   * 性別表示テキストを取得
   * APIドキュメントに基づく：患者列表APIのgenderは"男"または"女"
   */
  getGenderText(gender: string): string {
    if (gender === '男') return '(男)';
    if (gender === '女') return '(女)';
    return '';
  }

  /**
   * 患者名のスタイルクラスを取得
   */
  getNameClass(name: string): string {
    if (name.length > 10) return 'patient-name name-small';
    if (name.length > 6) return 'patient-name name-medium';
    return 'patient-name';
  }
}

